import { FC } from 'react';
import { useTranslation } from 'react-i18next';

import { FeedbackEvent } from '@bonusly/common-feedback';
import {
	ArchiveOutline,
	Box,
	ChatAltOutline,
	EmojiHappyOutline,
	GiftOutline,
	MegaphoneOutline,
	PlusSquareSolid,
	rem,
	Skeleton,
	TargetOutline,
	TrophyOutline,
} from '@bonusly/design-system';
import { useDispatch } from '@bonusly/events';
import { FeatureFlag, Permission, ProtectedContent } from '@bonusly/who-am-i';

import { useMakeAnnouncementModal } from '../../../components/announcements/hooks';
import { Reveal } from '../../../components/display/reveal';
import { GiveBoxEvent } from '../../../components/recognition/give-box/events';
import { useNominationsCount } from '../../../queries/nominations';
import { useAwardData } from '../../bonus-feed/features/recognition/components/Awards/hooks/useAwardData';
import { useAwardModal } from '../../bonus-feed/features/recognition/components/Awards/hooks/useAwardModal';
import { useCheckInModal } from '../../check-ins/hooks/use-checkin-modal';
import { useCreateGoalModal } from '../../growth/components/goals';
import { useNominationModal } from '../../nominations/hooks';
import { useCheckInsConfigured } from '../../team-management/hooks/use-check-ins-configured';
import { IconAndTextContent } from './icon-and-text-content';
import { UserNavAction } from './user-nav-action';
import { UserNavMenu } from './user-nav-menu';

type CreateNewMenuProps = {
	isExpanded: boolean;
};

export const CreateNewMenu: FC<CreateNewMenuProps> = ({ isExpanded }) => {
	const { t } = useTranslation('navigation');
	const dispatch = useDispatch();

	const { onOpen: handleOpenCheckInModal } = useCheckInModal();
	const { onOpen: handleOpenGoalModal } = useCreateGoalModal();
	const { onOpen: handleAwardModal } = useAwardModal();
	const { onOpen: handleNominateModal } = useNominationModal();
	const { onOpen: handleOpenAnnouncementModal } = useMakeAnnouncementModal();

	const checkInsEnabled = useCheckInsConfigured();
	const { shouldDisplayBanner: hasAwardsToGive, isLoading: isLoadingAwards } = useAwardData();
	const { open: nominationsOpen, isLoading: isLoadingNominations } = useNominationsCount();

	if (isLoadingAwards || isLoadingNominations) {
		return <Skeleton mt={rem(8)} height={rem(40)} width="100%" />;
	}

	return (
		<Box mt={rem(8)}>
			<UserNavMenu buttonIcon={<PlusSquareSolid size="md" color="blue.500" />} buttonText={t('createNewButtonText')} isExpanded={isExpanded}>
				{/* GIVE RECOGNITION */}
				<ProtectedContent permissions={Permission.CanGiveRecognition}>
					<UserNavAction onClick={() => dispatch(GiveBoxEvent.Open)} isExpanded data-testid="nav-action-recognition">
						<IconAndTextContent icon={<GiftOutline size="md" />} label={t('createNew.recognition')} isExpanded />
					</UserNavAction>
				</ProtectedContent>

				{/* CHECK IN */}
				<FeatureFlag flag="manager-enablement-check-in">
					<ProtectedContent permissions={Permission.CanAccessCheckIn}>
						<Reveal if={checkInsEnabled}>
							<UserNavAction onClick={() => handleOpenCheckInModal()} isExpanded data-testid="nav-action-check-in">
								<IconAndTextContent icon={<EmojiHappyOutline size="md" />} label={t('createNew.checkIn')} isExpanded />
							</UserNavAction>
						</Reveal>
					</ProtectedContent>
				</FeatureFlag>

				{/* ASK FOR ADVICE (FEEDBACK) */}
				<ProtectedContent permissions={Permission.CanAccessOneOnOnes}>
					<UserNavAction onClick={() => dispatch(FeedbackEvent.OpenRequest)} isExpanded data-testid="nav-action-advice-request">
						<IconAndTextContent icon={<ChatAltOutline size="md" />} label={t('createNew.adviceRequest')} isExpanded />
					</UserNavAction>
				</ProtectedContent>

				{/* ADD GOAL */}
				<ProtectedContent permissions={Permission.CanAccessGoals}>
					<UserNavAction onClick={handleOpenGoalModal} isExpanded data-testid="nav-action-goal">
						<IconAndTextContent icon={<TargetOutline size="md" />} label={t('createNew.goal')} isExpanded />
					</UserNavAction>
				</ProtectedContent>

				{/* AWARD */}
				<ProtectedContent permissions={Permission.CanAccessAwards}>
					<Reveal if={hasAwardsToGive}>
						<UserNavAction onClick={() => handleAwardModal()} isExpanded data-testid="nav-action-award">
							<IconAndTextContent icon={<TrophyOutline size="md" />} label={t('createNew.award')} isExpanded />
						</UserNavAction>
					</Reveal>
				</ProtectedContent>

				{/* NOMINATE TEAMMATE */}
				<ProtectedContent permissions={Permission.CanAccessNominations}>
					<Reveal if={nominationsOpen > 0}>
						<UserNavAction onClick={() => handleNominateModal()} isExpanded data-testid="nav-action-nomination">
							<IconAndTextContent icon={<ArchiveOutline size="md" />} label={t('createNew.nomination')} isExpanded />
						</UserNavAction>
					</Reveal>
				</ProtectedContent>

				{/* MAKE ANNOUNCEMENT */}
				<ProtectedContent permissions={[Permission.CanAccessAnnouncements, Permission.CanAdministerAnnouncements]}>
					<UserNavAction onClick={handleOpenAnnouncementModal} isExpanded data-testid="nav-action-announcement">
						<IconAndTextContent icon={<MegaphoneOutline size="md" />} label={t('createNew.announcement')} isExpanded />
					</UserNavAction>
				</ProtectedContent>
			</UserNavMenu>
		</Box>
	);
};
